import React, { createContext, useContext, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const NavigationContext = createContext(null);

// The "came from" trail rides along in location.state.navStack as an array of
// { path, label, restore? } entries. Because it lives on the history entry
// itself, browser back/forward restore the correct trail automatically, and a
// fresh navigation without state (sidebar click, direct URL) starts clean.
//
// entry.restore is a bag of per-surface state, merged into
// location.state.restore when navigating back to that entry, so a destination
// comes back as the user left it rather than as a fresh page. It is assembled
// from the RESTORE SOURCES registered by whatever is on screen at the moment
// the entry is created — see registerRestoreSource below and
// hooks/useRestoreSource.js.
export function NavigationProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const navStack = useMemo(
    () => (Array.isArray(location.state?.navStack) ? location.state.navStack : []),
    [location.state]
  );

  // ── Unsaved-changes navigation guard ─────────────────────────────────────
  // A page with unsaved work registers { isDirty, prompt } (one at a time —
  // only one page is ever mounted). Every navigation issued through this
  // context is funneled through runGuarded: when the guard reports dirty, the
  // navigation closure is handed to the page's prompt (which shows the
  // Leave/Stay confirmation and re-runs the closure on Leave) instead of
  // executing. This is the app's stand-in for React Router's useBlocker,
  // which requires a data router (we're on <BrowserRouter>).
  const guardRef = useRef(null);
  const registerNavigationGuard = useCallback((guard) => {
    guardRef.current = guard;
    return () => { if (guardRef.current === guard) guardRef.current = null; };
  }, []);
  const runGuarded = useCallback((go) => {
    const g = guardRef.current;
    if (g && g.isDirty()) { g.prompt(go); return; }
    go();
  }, []);
  // For navigation sources that deliberately bypass the trail (the sidebar's
  // clean-state navigations): wraps an arbitrary navigation closure.
  const guardedNavigate = runGuarded;

  // ── Restore sources ──────────────────────────────────────────────────
  // The surfaces currently on screen describe THEMSELVES, by name, through
  // hooks/useRestoreSource. When a trail entry is created, every registered
  // source is asked for its bag and the answers are stored on that entry — so
  // "← Back to X" hands each surface its own state back as
  // location.state.restore[name].
  //
  // WHY THE SOURCES AND NOT THE CALL SITE. `navigateWithBack(to, from)` made
  // the DEPARTING code responsible for snapshotting the DESTINATION's state.
  // That works exactly once — where a page navigates away from itself, which is
  // why Financials was the one page that restored properly — and cannot work
  // anywhere else, because the other eight call sites are leaving a page they
  // do not own. Inverting it means every existing call site gained restoration
  // without changing a line, and a new one gets it for free.
  //
  // More than one source is normal: the shell owns its panels and the window
  // scroll while the routed page owns its filters, and both are on screen at
  // once. Keyed by name so a remount replaces rather than duplicates.
  const restoreSourcesRef = useRef(new Map());
  const registerRestoreSource = useCallback((name, fn) => {
    restoreSourcesRef.current.set(name, fn);
    return () => {
      if (restoreSourcesRef.current.get(name) === fn) restoreSourcesRef.current.delete(name);
    };
  }, []);
  // A source that throws must not take the navigation down with it — failing to
  // remember a scroll offset is not a reason to refuse to move.
  const captureRestore = useCallback(() => {
    const bag = {};
    for (const [name, fn] of restoreSourcesRef.current) {
      try {
        const v = fn();
        if (v !== undefined && v !== null) bag[name] = v;
      } catch (err) {
        console.error(`Restore source "${name}" threw; continuing without it:`, err);
      }
    }
    return Object.keys(bag).length > 0 ? bag : null;
  }, []);

  // Navigate forward and push where we came from onto the trail. `from` is
  // { path, label, restore? }; opts.state merges extra state onto the target
  // location (e.g. cruncherScope, output).
  // A `from.restore` supplied by the call site still wins over a captured one
  // of the same name: a call site that hand-builds a bag is describing a moment
  // the generic capture cannot see, and it should keep that power.
  const navigateWithBack = useCallback((toPath, from, opts = {}) => {
    runGuarded(() => {
      const captured = captureRestore();
      const entry = (captured || from?.restore)
        ? { ...from, restore: { ...(captured || {}), ...(from?.restore || {}) } }
        : from;
      navigate(toPath, {
        replace: !!opts.replace,
        state: { ...(opts.state || {}), navStack: [...navStack, entry] },
      });
    });
  }, [navigate, navStack, runGuarded, captureRestore]);

  // Navigate carrying the current trail unchanged — used where the old code
  // changed pages without pushing or clearing the manual stack.
  const navigateKeepStack = useCallback((toPath, opts = {}) => {
    runGuarded(() => {
      navigate(toPath, {
        replace: !!opts.replace,
        state: { ...(opts.state || {}), navStack },
      });
    });
  }, [navigate, navStack, runGuarded]);

  // Pop the most recent entry and return to it ("← Back to [label]").
  const goBack = useCallback(() => {
    const prev = navStack[navStack.length - 1];
    if (!prev) return;
    runGuarded(() => {
      navigate(prev.path, {
        state: { navStack: navStack.slice(0, -1), restore: prev.restore },
      });
    });
  }, [navigate, navStack, runGuarded]);

  // Jump to trail entry at `index`; entries from index onward are dropped
  // (same truncation semantics as the old manual stack's navigateToIndex).
  const navigateToIndex = useCallback((index) => {
    const entry = navStack[index];
    if (!entry) return;
    runGuarded(() => {
      navigate(entry.path, {
        state: { navStack: navStack.slice(0, index), restore: entry.restore },
      });
    });
  }, [navigate, navStack, runGuarded]);

  // Walk UP to a path the trail already holds — what a RECORD BREADCRUMB does.
  // A breadcrumb and the back pill are different affordances pointing at the
  // same hierarchy, and if the breadcrumb navigated without touching the trail
  // the pill would end up offering to take you back to the page you are
  // standing on ("← Back to Dana Harper", on Dana Harper). So a breadcrumb jump
  // is exactly goBack-to-that-entry: everything after it is dropped and its
  // restore bag comes with it.
  //
  // When the path is NOT on the trail — a deep link straight into a record, or
  // a breadcrumb climbing above wherever the user entered — it degrades to an
  // ordinary navigation that leaves the trail alone, which is the behaviour
  // every one of these buttons had before.
  const navigateUpTo = useCallback((toPath) => {
    for (let i = navStack.length - 1; i >= 0; i--) {
      if (navStack[i]?.path === toPath) { navigateToIndex(i); return; }
    }
    navigateKeepStack(toPath);
  }, [navStack, navigateToIndex, navigateKeepStack]);

  const previousPage = navStack.length > 0 ? navStack[navStack.length - 1] : null;

  const value = useMemo(() => ({
    navigationHistory: navStack,
    previousPage,
    navigateWithBack,
    navigateKeepStack,
    goBack,
    navigateToIndex,
    navigateUpTo,
    registerNavigationGuard,
    registerRestoreSource,
    guardedNavigate,
  }), [navStack, previousPage, navigateWithBack, navigateKeepStack, goBack, navigateToIndex, navigateUpTo, registerNavigationGuard, registerRestoreSource, guardedNavigate]);

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const ctx = useContext(NavigationContext);
  if (!ctx) throw new Error('useNavigation must be used within NavigationProvider');
  return ctx;
}
